/**
 * CLI Workflow Creation
 *
 * Creates a workflow with nodes and edges atomically via the API.
 * Supports reading workflow JSON from stdin or file.
 *
 * @module workflow-create
 */

import fs from "node:fs/promises";
import process from "node:process";
import { getArgValue, hasFlag, resolveAppUrl } from "./auth.js";
import { renderWorkflowUserError, type CredentialListItem } from "./cli-helpers.js";
import { fetchOrExit } from "./api-fetch.js";
import { applyWorkflowTagFlags } from "./tag.js";
import { normalizeNodeType, NODE_TYPE_LABEL } from "@chatsdet/types";

/* ── Types ────────────────────────────────────────────────────────────── */

type WorkflowNodeInput = {
  nodeType: string;
  configJson?: Record<string, unknown>;
};

type WorkflowCreateInput = {
  name: string;
  description?: string;
  status?: "draft" | "approved";
  flowType?: "standard" | "setup" | "seed" | "teardown";
  createdVia?: string;
  propertyId?: string;
  credentialId?: string;
  nodes: WorkflowNodeInput[];
};

type CreateResponse = {
  ok: boolean;
  error?: string;
  /** Set on structured user errors (e.g. WORKFLOW_VALIDATION_FAILED). */
  code?: string;
  /** Per-node validation issues, surfaced via renderWorkflowUserError. */
  issues?: unknown[];
  warnings?: string[];
  workflow?: {
    id: string;
    name: string;
    status: string;
    flowType: string;
    createdVia: string | null;
  };
  nodes?: Array<{ id: string; nodeType: string; orderIndex: number }>;
  edges?: Array<{ id: string; sourceNodeId: string; targetNodeId: string }>;
};

/* ── Input Reading ───────────────────────────────────────────────────── */

async function readFromStdin(): Promise<string> {
  const chunks: Buffer[] = [];
  const stdin = process.stdin;

  if (stdin.isTTY) {
    throw new Error("No input on stdin. Pipe workflow JSON or use --from-file.");
  }

  return new Promise((resolve, reject) => {
    stdin.on("data", (chunk) => {
      chunks.push(typeof chunk === "string" ? Buffer.from(chunk) : chunk);
    });
    stdin.on("end", () => resolve(Buffer.concat(chunks).toString("utf-8")));
    stdin.on("error", reject);
  });
}

async function readWorkflowInput(argv: string[]): Promise<WorkflowCreateInput> {
  let raw: string;

  const fromFile = getArgValue(argv, "--from-file");
  if (fromFile) {
    try {
      raw = await fs.readFile(fromFile, "utf-8");
    } catch (err) {
      throw new Error(`Failed to read file ${fromFile}: ${err instanceof Error ? err.message : String(err)}`);
    }
  } else {
    raw = await readFromStdin();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Invalid JSON input. Please provide valid workflow JSON.");
  }

  const input = parsed as WorkflowCreateInput;

  if (!input.name || typeof input.name !== "string") {
    throw new Error('Workflow JSON must include a "name" string field.');
  }

  if (!Array.isArray(input.nodes) || input.nodes.length === 0) {
    throw new Error('Workflow JSON must include a non-empty "nodes" array.');
  }

  // Source the allowlist from the canonical node-type set so it never drifts
  // from the server enum (`workflowNodeTypeEnum`). NODE_TYPE_LABEL's keys are
  // that set — including sandbox_action, audio, and audio-assertion.
  const validNodeTypes = Object.keys(NODE_TYPE_LABEL);
  for (const node of input.nodes) {
    // Accept "acting-as" as an alias for "login" — keep persisted type canonical.
    if (typeof node.nodeType === "string") {
      node.nodeType = normalizeNodeType(node.nodeType);
    }
    if (!node.nodeType || !validNodeTypes.includes(node.nodeType)) {
      throw new Error(
        `Invalid nodeType "${node.nodeType}". Valid types: ${validNodeTypes.join(", ")}`
      );
    }
  }

  return input;
}

/* ── Credential Resolution ──────────────────────────────────────────── */

async function fetchOrgCredentials(
  apiUrl: string,
  token: string
): Promise<CredentialListItem[]> {
  const res = await fetch(`${apiUrl}/org/credentials`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = (await res.json()) as { ok: boolean; items: CredentialListItem[] };
  return (data.items ?? []).filter((c) => !c.isEphemeral);
}

/**
 * Resolve a credential for login nodes that are missing credentialId.
 * Resolution order:
 *   1. Match by credentialName (if provided)
 *   2. Pick the default credential (isDefault: true)
 *   3. Pick the only non-local credential (if exactly one)
 *   4. Error with available credentials listed so the caller can retry
 */
async function resolveCredential(
  apiUrl: string,
  token: string,
  credentialName?: string
): Promise<{ credentialId: string; credentialName: string; propertyId?: string }> {
  const credentials = await fetchOrgCredentials(apiUrl, token);

  if (credentials.length === 0) {
    throw new Error(
      "No credentials found for this organization. Create one in the web app first."
    );
  }

  // 1. Match by name if provided
  if (credentialName) {
    const match = credentials.find(
      (c) => c.name.toLowerCase() === credentialName.toLowerCase()
    );
    if (match) {
      const isLocal = /\blocal\b/i.test(match.name);
      if (isLocal) {
        console.warn(
          `Warning: Credential "${match.name}" appears to be local-only. ` +
          `The workflow may fail in deployed environments. Consider using a non-local credential.`
        );
      }
      return { credentialId: match.id, credentialName: match.name, propertyId: match.propertyId };
    }
    // Name didn't match — fall through to other strategies
    console.warn(`Warning: No credential matching name "${credentialName}". Trying other resolution strategies.`);
  }

  // 2. Pick the default credential
  const defaults = credentials.filter((c) => c.isDefault);
  if (defaults.length === 1) {
    console.log(`Auto-selected default credential: ${defaults[0].name} (${defaults[0].id})`);
    return { credentialId: defaults[0].id, credentialName: defaults[0].name, propertyId: defaults[0].propertyId };
  }

  // 3. Pick the only non-local credential
  const nonLocal = credentials.filter((c) => !/\blocal\b/i.test(c.name));
  if (nonLocal.length === 1) {
    console.log(`Auto-selected credential: ${nonLocal[0].name} (${nonLocal[0].id})`);
    return { credentialId: nonLocal[0].id, credentialName: nonLocal[0].name, propertyId: nonLocal[0].propertyId };
  }

  // 4. Ambiguous — list credentials and error
  const lines = credentials.map(
    (c) => `  ${c.id}  ${c.name}${c.isDefault ? " (default)" : ""}${c.propertyName ? ` — ${c.propertyName}` : ""}`
  );
  throw new Error(
    `Cannot auto-resolve credential. Specify credentialId in the login node configJson.\n\n` +
    `Available credentials:\n${lines.join("\n")}`
  );
}

/* ── API-only workflow branch ────────────────────────────────────────── */

type ApiSequenceConfig = {
  steps: unknown[];
  description?: string;
  stopOnFailure?: boolean;
  exports?: unknown[];
};

async function readApiSequenceConfig(argv: string[]): Promise<ApiSequenceConfig> {
  const fromFile = getArgValue(argv, "--sequence-from-file");
  const fromStdin = hasFlag(argv, "--sequence-from-stdin");

  if (!fromFile && !fromStdin) {
    return { steps: [] };
  }

  let raw: string;
  if (fromFile) {
    raw = await fs.readFile(fromFile, "utf-8");
  } else {
    raw = await readFromStdin();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error("Invalid JSON for sequence config.");
  }

  const cfg = parsed as ApiSequenceConfig;
  if (!cfg || !Array.isArray(cfg.steps)) {
    throw new Error('Sequence config must be { "steps": [...] }.');
  }
  return cfg;
}

async function handleApiOnlyCreate(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const name = getArgValue(argv, "--name");
  if (!name) {
    console.error('Error: --api-only requires --name <workflow name>.');
    process.exit(1);
  }

  const credentialId = getArgValue(argv, "--credential");
  const guidance = getArgValue(argv, "--guidance");

  let config: ApiSequenceConfig;
  try {
    config = await readApiSequenceConfig(argv);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  const body: Record<string, unknown> = { name, config };
  if (credentialId) body.credentialId = credentialId;
  if (guidance) body.guidancePrompt = guidance;

  const res = await fetchOrExit(`${apiUrl}/api-sequences/create-with-workflow`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    token,
    body: JSON.stringify(body),
  });

  const raw = (await res.json().catch(() => ({}))) as {
    ok: boolean;
    error?: string;
    code?: string;
    issues?: Array<{ path: string; code: string; message: string; hint?: string }>;
    data?: { workflowId: string; apiSequenceId: string; nodeId?: string };
  };

  if (!res.ok || !raw.ok || !raw.data) {
    const jsonOutput = hasFlag(argv, "--json") || !process.stdout.isTTY;
    if (raw.code === 'API_SEQUENCE_INVALID' && Array.isArray(raw.issues)) {
      if (jsonOutput) {
        console.log(
          JSON.stringify(
            {
              ok: false,
              error: raw.error ?? 'API sequence configuration is invalid',
              code: 'API_SEQUENCE_INVALID',
              issueCount: raw.issues.length,
              issues: raw.issues,
            },
            null,
            2,
          ),
        );
      } else {
        console.error(`Error: ${raw.error ?? 'API sequence configuration is invalid'}`);
        console.error(`Found ${raw.issues.length} issue${raw.issues.length === 1 ? '' : 's'}:`);
        console.error('');
        for (const issue of raw.issues) {
          console.error(`  ${issue.path || '(root)'} [${issue.code}] :: ${issue.message}`);
          if (issue.hint) console.error(`    hint: ${issue.hint}`);
        }
      }
      process.exit(1);
    }
    console.error(`Error: ${raw.error ?? `${res.status} ${res.statusText}`}`);
    process.exit(1);
  }

  if (hasFlag(argv, "--json") || !process.stdout.isTTY) {
    console.log(JSON.stringify({ ok: true, data: raw.data }, null, 2));
    return;
  }

  const appUrl = resolveAppUrl(apiUrl);
  console.log(`\n\u2713 API-only workflow created: ${name}`);
  console.log(`  Workflow ID:     ${raw.data.workflowId}`);
  console.log(`  API Sequence ID: ${raw.data.apiSequenceId}`);
  if (raw.data.nodeId) console.log(`  Node ID:         ${raw.data.nodeId}`);
  console.log(`  Steps:           ${config.steps.length}`);
  console.log(`  URL:             ${appUrl}/flows/${raw.data.workflowId}`);
  console.log(`\n  Next:`);
  console.log(`    canary workflow api-sequence get ${raw.data.workflowId}`);
  console.log(`    canary workflow api-sequence update ${raw.data.workflowId} --from-file seq.json`);
  console.log(`    canary workflow api-sequence execute ${raw.data.workflowId}`);
}

/* ── Main Handler ────────────────────────────────────────────────────── */

export async function handleWorkflowCreate(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  if (hasFlag(argv, "--api-only")) {
    await handleApiOnlyCreate(argv, apiUrl, token);
    return;
  }

  // Auto-publish was removed with the approval/eligibility lifecycle split:
  // a human approves, and a flow becomes eligible for suites once it has also
  // passed at least once (no explicit promote step).
  if (hasFlag(argv, "--run-and-publish")) {
    console.error(
      "Error: --run-and-publish was removed. Run the test (canary workflow run <id>) and\n" +
        "approve it (canary workflow approve <id> --yes); it becomes eligible for suites once it has passed."
    );
    process.exit(1);
  }

  // 1. Read and validate input
  let input: WorkflowCreateInput;
  try {
    input = await readWorkflowInput(argv);
  } catch (err) {
    console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  // 2. Resolve credentials for login nodes missing credentialId
  const loginNodes = input.nodes.filter((n) => n.nodeType === "login");
  for (const loginNode of loginNodes) {
    const config = loginNode.configJson ?? {};
    if (!config.credentialId && !input.credentialId) {
      try {
        const resolved = await resolveCredential(
          apiUrl,
          token,
          config.credentialName as string | undefined
        );
        config.credentialId = resolved.credentialId;
        config.credentialName = resolved.credentialName;
        loginNode.configJson = config;
        if (resolved.propertyId && !input.propertyId) {
          input.propertyId = resolved.propertyId;
        }
      } catch (err) {
        console.error(`Error: ${err instanceof Error ? err.message : String(err)}`);
        process.exit(1);
      }
    }
  }

  // All flows start as drafts — approval is an explicit follow-up step
  // (canary workflow approve). A flow becomes eligible for suites once it is
  // approved AND has passed at least once. Reliability is tracked separately
  // by the workflow's `health` (starts at `unverified`).
  input.createdVia = "cli";

  // 4. Create workflow via API
  const res = await fetchOrExit(`${apiUrl}/workflows/create-with-nodes`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    token,
    body: JSON.stringify(input),
  });

  const result = (await res.json()) as CreateResponse;

  if (!result.ok || !result.workflow) {
    // Surface per-node validation detail (WORKFLOW_VALIDATION_FAILED carries an
    // `issues` array) instead of the bare catalog message. Same renderer the
    // `workflow run` path uses.
    const rendered = renderWorkflowUserError(result as unknown as Record<string, unknown>);
    console.error(rendered ?? `Error: ${result.error ?? "Failed to create workflow"}`);
    process.exit(1);
  }

  const { workflow, nodes, edges } = result;

  const { warnings } = result;

  console.log(`\n\u2713 Workflow created: ${workflow.name}`);
  console.log(`  ID: ${workflow.id}`);
  console.log(`  Status: ${workflow.status}`);
  console.log(`  Nodes: ${nodes?.length ?? 0}`);
  console.log(`  Edges: ${edges?.length ?? 0}`);

  if (warnings && warnings.length > 0) {
    console.log(`\n  Warnings:`);
    for (const w of warnings) {
      console.log(`    \u26A0 ${w}`);
    }
  }

  const appUrl = resolveAppUrl(apiUrl);
  console.log(`  URL: ${appUrl}/flows/${workflow.id}`);

  // 5. Apply --tags / --tag-id if provided.
  try {
    const tagResult = await applyWorkflowTagFlags({
      apiUrl,
      token,
      argv,
      workflowId: workflow.id,
    });
    if (tagResult) {
      console.log(`  Tags: ${tagResult.tagIds.length} assigned`);
    }
  } catch (err) {
    console.error(`\nError applying tags:\n${err instanceof Error ? err.message : String(err)}`);
    process.exit(1);
  }

  console.log(
    `\n  Next: run it (canary workflow run ${workflow.id}) and approve it ` +
      `(canary workflow approve ${workflow.id} --yes). Once approved and passing, ` +
      `it becomes eligible for suite runs.`
  );
}
